// ** Modules
import React from 'react';
import { setCookie } from 'nookies';
import { useFormik } from 'formik';
import { useRouter } from 'next/router';
// import { useSession } from 'next-auth/react';
import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'next-i18next/pages';

// ** UI
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import Alert from '@mui/material/Alert';
import ListItem from '@mui/material/ListItem';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

// ** Icons
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';

// ** Components
import Button from '@/components/common/Button';
import PriceInfo from '@/features/events/book/PriceInfo';
import FormikTextField from '@/components/forms/formik/FormikTextField';

// ** Utils
import { extractEventDetails } from '@/utils/domain/eventUtils';

// ** Apollo
import { postEventGuestRequestMutation } from '@/lib/utils/eventDataUtils';

// ** Types
import type { User } from 'next-auth';
import type { Event } from '@/types/graphql';
import type { Theme } from '@mui/material/styles';
import type { GuestRequestResponse } from '@/types/graphql';
import type { EventBookingGuestFormData } from '@/types/formik';

type EventBookingGuestRequestResponse = {
	eventGuestRequest: GuestRequestResponse;
};

type EventBookingGuestProps = {
	currentUser?: User | null;
	eventById: Event;
};

const EventBookingGuest = ({ currentUser, eventById }: EventBookingGuestProps) => {
	// Media Queires
	const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

	// Hooks
	const theme = useTheme();
	const router = useRouter();
	// const { data: session } = useSession();

	// States
	const [showAlert, setShowAlert] = React.useState(false);

	// Translation
	const { t } = useTranslation();

	// Apollo - Mutations
	const [postEventRequest, { loading: loadingMutation, data: mutationResponse }] = useMutation<
		EventBookingGuestRequestResponse,
		{
			data: {
				id: string | number;
				lastName: string;
				firstName: string;
				guestEmail: string;
				notes: string;
				telephone?: string;
				company?: string;
				eventTitle: string;
				eventNumber: string;
				invoice?: {
					invoiceRecipient: string;
					street: string;
					houseNumber: string;
					postalCode: string;
					city: string;
				};
			};
		}
	>(postEventGuestRequestMutation);

	const eventDetails = React.useMemo(() => extractEventDetails(eventById), [eventById]);
	const {
		title,
		eventNumber,
		appointmentInfo,
		timeOfDay,
		memberPrice,
		memberPriceAdditionalPerson,
		notMemberPrice,
		notMemberPriceAdditionalPerson,
		eventPlace,
		onlyForMembers,
		priceWithoutTax,
		priceCondition,
		field
	} = eventDetails || {};

	const isNormalPrice = priceCondition === 'Normal';
	const isFreeForMembers = priceCondition === 'Mitglieder Kostenlos';
	const isFreeForEveryone = priceCondition === 'Kostenlos';
	const isInvoiceStepIncluded = !(
		priceCondition === 'Kostenlos' ||
		(priceCondition === 'Mitglieder Kostenlos' && currentUser?.isMember)
	);

	const initialValues = {
		lastName: '',
		firstName: '',
		guestEmail: '',
		notes: '',
		telephone: '',
		company: '',
		invoice: isInvoiceStepIncluded
			? {
					email: '',
					invoiceRecipient: '',
					street: '',
					houseNumber: '',
					postalCode: '',
					city: ''
				}
			: undefined,
		acceptTerms: false
	};

	const formik = useFormik({
		initialValues,
		onSubmit: async (values) => {
			// Create requestData based on form values and event details
			const requestData: EventBookingGuestFormData = {
				id: eventById.id,
				lastName: values.lastName,
				firstName: values.firstName,
				guestEmail: values.guestEmail,
				notes: values.notes,
				telephone: values.telephone,
				company: values.company,
				eventTitle: title as string,
				eventNumber: eventNumber as string,
				eventStartDate: appointmentInfo || ''
			};

			if (isInvoiceStepIncluded) {
				requestData.invoice = {
					email: values?.invoice?.email as string,
					invoiceRecipient: values?.invoice?.invoiceRecipient as string,
					street: values?.invoice?.street as string,
					houseNumber: values?.invoice?.houseNumber as string,
					postalCode: values?.invoice?.postalCode as string,
					city: values?.invoice?.city as string
				};
			}

			try {
				const { data } = await postEventRequest({
					variables: {
						data: requestData
					}
				});

				// Optionally, redirect or show a success message here
				if (data?.eventGuestRequest.success) {
					// Save form data to cookies before redirecting
					setCookie(
						null,
						'tischlerSuccess',
						JSON.stringify([
							{
								successContent: { ...requestData },
								component: 'GuestBookSuccess'
							}
						]),
						{
							path: '/',
							maxAge: 5 * 60 * 60
						}
					);

					router.push({
						pathname: '/success'
					});
					// router.push({
					// 	pathname: '/success',
					// 	query: requestData
					// });
				} else {
					// Handle case where success is false
					setShowAlert(true);
				}
			} catch (error) {
				console.error('Error submitting request:', error);
				setShowAlert(true);
			}
		},
		validate: (values) => {
			const errors: any = {};
			if (!values.firstName) errors.firstName = t('common:required');
			if (!values.lastName) errors.lastName = t('common:required');
			if (!values.guestEmail) errors.guestEmail = t('common:required');

			if (isInvoiceStepIncluded) {
				const invoiceErrors: Record<string, string> = {};

				if (!values?.invoice?.email) invoiceErrors.email = t('common:required');
				if (!values?.invoice?.invoiceRecipient) invoiceErrors.invoiceRecipient = t('common:required');
				if (!values?.invoice?.street) invoiceErrors.street = t('common:required');
				if (!values?.invoice?.houseNumber) invoiceErrors.houseNumber = t('common:required');
				if (!values?.invoice?.postalCode) invoiceErrors.postalCode = t('common:required');
				if (!values?.invoice?.city) invoiceErrors.city = t('common:required');

				if (Object.keys(invoiceErrors).length > 0) {
					errors.invoice = invoiceErrors;
				}
			}

			return errors;
		}
	});

	/**
	 * Checks if a price is valid (greater than 0).
	 * @param price - The price value to check.
	 * @returns True if the price is greater than 0, otherwise false.
	 */
	const isPriceValid = (price: number | null | undefined): boolean => {
		return price !== undefined && price !== null && price > 0;
	};

	// Check if the event is members-only (i.e., only a member price is set and no non-member price)
	const isMembersOnly =
		(memberPrice || memberPriceAdditionalPerson) && !notMemberPrice && !notMemberPriceAdditionalPerson;

	React.useEffect(() => {
		// Redirect if the event is for members only, the user is logged in, but not a member
		if (isMembersOnly && currentUser && !currentUser?.isMember) {
			router.back();
		}
	}, [currentUser, isMembersOnly, router]);

	// TODO: Redirect the user to the user booking system when user is logged, fix this after the user booking system is ready, note: eventbyId is an object
	// React.useEffect(() => {
	// 	if (session?.user) {
	// 		router.push(`/events/${eventById}`); // TODO: Update this path to the actual user booking system path, also make sure to pass necessary data (e.g., event ID) to the booking system, maybe via query params or state
	// 	}
	// }, [session, eventById, router.push]);

	return (
		<React.Fragment>
			{isMembersOnly && !currentUser?.isMember ? (
				<Grid container spacing={4}>
					<Grid container size={{ xs: 8 }}>
						<Grid sx={{ mb: 10 }}>
							<Typography component="h1" variant="h3-primary">
								{t('common:only_for_members')}
							</Typography>
						</Grid>
					</Grid>
				</Grid>
			) : (
				<Box
					sx={{
						display: 'flex',
						flexDirection: { xs: 'column', lg: 'row' },
						gap: { xs: 4, lg: 24 },
						alignItems: 'flex-start'
					}}
				>
					{/* Left/Top - Infos */}
					<Box
						sx={{
							width: { xs: '100%', lg: '65%' },
							minWidth: 0,
							flexShrink: 0
						}}
					>
						{/* Event Title */}
						<Typography
							variant="title-lg"
							component="h1"
							sx={{
								color: theme.palette.common.black,
								fontSize: { xs: '1.75rem', md: '2.5rem' },
								lineHeight: { xs: '2.25rem', md: '3rem' },
								mb: { xs: 4, lg: 22 }
							}}
						>
							{title}
						</Typography>

						{isMobile && (
							<Typography component="h2" variant="h6-normal">
								{eventNumber}
							</Typography>
						)}

						{/* Formik Form */}
						<form onSubmit={formik.handleSubmit}>
							<Card elevation={3} sx={{ p: 4 }}>
								<CardHeader
									title={t('form:participant_info')}
									slotProps={{
										variant: 'h5-normal'
									}}
								/>
								<CardContent>
									<Grid container spacing={2}>
										{/* First Name */}
										<Grid size={{ xs: 12, md: 6 }}>
											<FormikTextField
												fullWidth
												label={t('common:first_name')}
												name="firstName"
												value={formik.values.firstName}
												onChange={formik.handleChange}
												error={Boolean(formik.errors.firstName)}
												helperText={formik.errors.firstName}
												formikProps={formik}
											/>
										</Grid>

										{/* Last Name */}
										<Grid size={{ xs: 12, md: 6 }}>
											<FormikTextField
												fullWidth
												label={t('common:last_name')}
												name="lastName"
												value={formik.values.lastName}
												onChange={formik.handleChange}
												error={Boolean(formik.errors.lastName)}
												helperText={formik.errors.lastName}
												formikProps={formik}
											/>
										</Grid>

										{/* Email */}
										<Grid size={{ xs: 12, md: 6 }}>
											<FormikTextField
												fullWidth
												label={t('common:email')}
												name="guestEmail"
												value={formik.values.guestEmail}
												onChange={formik.handleChange}
												error={Boolean(formik.errors.guestEmail)}
												helperText={formik.errors.guestEmail}
												formikProps={formik}
											/>
										</Grid>

										{/* Phone (Optional) */}
										<Grid size={{ xs: 6 }}>
											<FormikTextField
												fullWidth
												label={`${t('common:telephone')} (${t('common:optional')})`}
												name="telephone"
												value={formik.values.telephone}
												onChange={formik.handleChange}
												formikProps={formik}
											/>
										</Grid>

										{/* Company (Optional) */}
										<Grid size={{ xs: 12 }}>
											<FormikTextField
												fullWidth
												label={`${t('common:company')} (${t('common:optional')})`}
												name="company"
												value={formik.values.company}
												onChange={formik.handleChange}
												formikProps={formik}
											/>
										</Grid>

										{/* Notes (Optional) */}
										<Grid size={{ xs: 12 }}>
											<FormikTextField
												fullWidth
												label={`${t('form:additionalParticipants')} (${t('common:optional')})`}
												name="notes"
												value={formik.values.notes}
												onChange={formik.handleChange}
												error={Boolean(formik.errors.notes && formik.touched.notes)} // Error handling
												helperText={formik.touched.notes && formik.errors.notes} // Display helper text if touched and error exists
												multiline
												rows={6}
												formikProps={formik}
											/>
										</Grid>
									</Grid>
								</CardContent>
							</Card>

							{/* Invoice Step */}
							{isInvoiceStepIncluded && (
								<Card elevation={3} sx={{ p: 4, mt: 10 }}>
									<CardHeader
										title={t('form:invoice_info')}
										slotProps={{
											variant: 'h5-normal'
										}}
									/>

									<CardContent>
										<Grid container spacing={2}>
											{/* Invoice Recipient */}
											<Grid container size={{ xs: 12 }} spacing={2}>
												<Grid size={{ xs: 6 }}>
													<FormikTextField
														name="invoice.invoiceRecipient"
														label={t('form:invoice_recipient')}
														formikProps={formik}
														fullWidth
														value={formik.values.invoice?.invoiceRecipient}
													/>
												</Grid>
											</Grid>

											{/* Invoice Recipient Email */}
											<Grid container size={{ xs: 12 }} spacing={2}>
												<Grid size={{ xs: 6 }}>
													<FormikTextField
														name="invoice.email"
														label={t('form:invoice_recipient_email')}
														formikProps={formik}
														fullWidth
														value={formik.values.invoice?.email}
													/>
												</Grid>
											</Grid>

											{/* Street, House Number, Postal Code, City */}
											<Grid container size={{ xs: 4 }} spacing={2}>
												{/* Street */}
												<Grid size={{ xs: 6 }}>
													<FormikTextField
														name="invoice.street"
														label={t('common:street')}
														formikProps={formik}
														fullWidth
														defaultValue={formik.values.invoice?.street}
													/>
												</Grid>

												{/* House Number */}
												<Grid size={{ xs: 6 }}>
													<FormikTextField
														name="invoice.houseNumber"
														label={t('common:house_number')}
														formikProps={formik}
														fullWidth
														defaultValue={formik.values.invoice?.houseNumber}
													/>
												</Grid>

												{/* Postal Code */}
												<Grid size={{ xs: 6 }}>
													<FormikTextField
														name="invoice.postalCode"
														label={t('common:postal_code')}
														formikProps={formik}
														fullWidth
														defaultValue={formik.values.invoice?.postalCode}
													/>
												</Grid>

												{/* City */}
												<Grid size={{ xs: 6 }}>
													<FormikTextField
														name="invoice.city"
														label={t('common:city')}
														formikProps={formik}
														fullWidth
														defaultValue={formik.values.invoice?.city}
													/>
												</Grid>
											</Grid>
										</Grid>
									</CardContent>
								</Card>
							)}
						</form>
					</Box>

					{/* Right/Bottom - Panel */}
					<Grid container size={{ xs: 12, md: 4 }}>
						<Grid
							sx={{
								top: 85,
								position: 'sticky',
								textAlign: 'center'
							}}
						>
							{!isMobile && (
								<Typography
									component="h2"
									variant="h3-normal"
									sx={{
										mb: 5,
										textAlign: 'center'
									}}
								>
									{eventNumber}
								</Typography>
							)}

							<Card>
								<CardHeader
									title={
										<React.Fragment>
											{isMobile && (
												<Typography component="h4" variant="h6-primary">
													{title}
												</Typography>
											)}

											<Typography component="h4" variant="h6-normal">
												{field}
											</Typography>
										</React.Fragment>
									}
									sx={{ pb: 0 }}
									slotProps={{
										variant: 'h6-normal',
										textAlign: 'start'
									}}
								/>

								{appointmentInfo || timeOfDay || eventPlace ? (
									<CardContent>
										<List disablePadding>
											{appointmentInfo && (
												<ListItem disableGutters>
													<ListItemIcon>
														<CalendarTodayOutlinedIcon />
													</ListItemIcon>
													<ListItemText primary={appointmentInfo} />
												</ListItem>
											)}

											{timeOfDay && (
												<ListItem disableGutters>
													<ListItemIcon>
														<AccessTimeIcon />
													</ListItemIcon>
													<ListItemText primary={timeOfDay} />
												</ListItem>
											)}

											{eventPlace && (
												<ListItem disableGutters>
													<ListItemIcon>
														<LocationOnOutlinedIcon />
													</ListItemIcon>
													<ListItemText primary={eventPlace} />
												</ListItem>
											)}
										</List>
									</CardContent>
								) : null}

								{/* Prices & Booking Button */}
								<CardActions sx={{ p: '0 1rem 1rem 1rem' }}>
									<Grid container spacing={2}>
										{onlyForMembers && (
											<Grid size={{ xs: 12 }}>
												<Typography
													variant="h5-primary"
													color="error"
													sx={{
														textAlign: 'start'
													}}
												>
													{t('common:only_for_members')}
												</Typography>
											</Grid>
										)}

										<Grid size={{ xs: 12 }}>
											<Typography
												variant="h5-primary"
												sx={{
													textAlign: 'start'
												}}
											>
												{isFreeForEveryone
													? t('common:free')
													: isFreeForMembers
														? t('common:member_free')
														: t('common:price_other')}
											</Typography>
										</Grid>

										{/* Detailed price breakdown */}
										{!isFreeForEveryone && (
											<React.Fragment>
												{/* Member Price */}
												<Grid size={{ xs: 6 }}>
													<PriceInfo
														label={t('common:member_one')}
														value={
															isFreeForEveryone || isFreeForMembers
																? null
																: isPriceValid(memberPrice)
																	? `€ ${memberPrice}`
																	: null
														}
														extraPersonLabel={t('common:extra_person')}
														priceWithoutTax={priceWithoutTax as boolean}
														highlight={currentUser?.isMember as boolean}
														extraPersonValue={
															isNormalPrice &&
															isPriceValid(memberPrice) &&
															isPriceValid(memberPriceAdditionalPerson) &&
															memberPrice !== memberPriceAdditionalPerson
																? `€ ${memberPriceAdditionalPerson}`
																: null
														}
													/>
												</Grid>

												{/* Not Member Price */}
												<Grid size={{ xs: 6 }}>
													<PriceInfo
														label={t('common:not_member')}
														value={
															isFreeForEveryone
																? null
																: isFreeForMembers
																	? isPriceValid(notMemberPrice)
																		? `€ ${notMemberPrice}`
																		: null
																	: isPriceValid(notMemberPrice)
																		? `€ ${notMemberPrice}`
																		: null
														}
														extraPersonLabel={t('common:extra_person')}
														priceWithoutTax={priceWithoutTax as boolean}
														highlight={!currentUser?.isMember}
														extraPersonValue={
															isNormalPrice &&
															isPriceValid(notMemberPrice) &&
															isPriceValid(notMemberPriceAdditionalPerson) &&
															notMemberPrice !== notMemberPriceAdditionalPerson
																? `€ ${notMemberPriceAdditionalPerson}`
																: null
														}
													/>
												</Grid>
											</React.Fragment>
										)}

										{/* Booking Button */}
										<Grid size={{ xs: 12 }} sx={{ mt: 5 }}>
											<Button
												successful={mutationResponse?.eventGuestRequest.success}
												successfulLabel={t('common:successful')}
												loading={loadingMutation}
												variant="outlined"
												fullWidth
												onClick={() => formik.handleSubmit()}
												disabled={!(formik.isValid && formik.dirty) || mutationResponse?.eventGuestRequest.success}
											>
												<ShoppingCartOutlinedIcon /> {t('common:book')}
											</Button>
										</Grid>
									</Grid>
								</CardActions>
							</Card>

							{showAlert && (
								<Alert severity="error" sx={{ mt: 10 }} onClose={() => setShowAlert(false)}>
									{t('form:submission_error', 'Beim Absenden Ihrer Buchung ist ein Fehler aufgetreten.')}
								</Alert>
							)}
						</Grid>
					</Grid>
				</Box>
			)}
		</React.Fragment>
	);
};

export default EventBookingGuest;
